/**
 * TypeBox schemas for User and UserSettings entities
 */

import { Type, type Static } from 'typebox'
import { UUID, ISODate, Email, Coordinates, type Exact } from './common.js'

// --- UserSettings ---

export const CoordinatePrecisionSchema = Type.Object({
  ddDecimals: Type.Optional(Type.Number()),
  dmsSecondDecimals: Type.Optional(Type.Number()),
  directionFormat: Type.Optional(Type.Union([Type.Literal('signed'), Type.Literal('cardinal')])),
  mgrsPrecision: Type.Optional(
    Type.Union([
      Type.Literal(1),
      Type.Literal(2),
      Type.Literal(3),
      Type.Literal(4),
      Type.Literal(5),
    ])
  ),
  utmShowZone: Type.Optional(Type.Boolean()),
})

export const SyncPreferencesSchema = Type.Object({
  wifi: Type.Boolean(),
  cellular: Type.Boolean(),
})

export const NotificationsSchema = Type.Object({
  emergency: Type.Boolean(),
  messages: Type.Boolean(),
})

export const PrivacySchema = Type.Object({
  shareLocation: Type.Union([
    Type.Literal('none'),
    Type.Literal('approximate'),
    Type.Literal('exact'),
  ]),
})

export const ContactSharingSchema = Type.Object({
  shareEmail: Type.Union([Type.Literal('always'), Type.Literal('never'), Type.Literal('ask')]),
  sharePhone: Type.Union([Type.Literal('always'), Type.Literal('never'), Type.Literal('ask')]),
  shareEmergency: Type.Union([Type.Literal('always'), Type.Literal('never'), Type.Literal('ask')]),
})
export type ContactSharing = Exact<Static<typeof ContactSharingSchema>>

export const TrackingSchema = Type.Object({
  trackQuality: Type.Union([
    Type.Literal('high'),
    Type.Literal('standard'),
    Type.Literal('extended'),
  ]),
})

export const UserSettingsSchema = Type.Object({
  theme: Type.Union([Type.Literal('light'), Type.Literal('dark'), Type.Literal('auto')]),
  coordinateFormat: Type.Union([
    Type.Literal('DD'),
    Type.Literal('DMS'),
    Type.Literal('DDM'),
    Type.Literal('MGRS'),
    Type.Literal('UTM'),
  ]),
  coordinatePrecision: Type.Optional(CoordinatePrecisionSchema),
  unitSystem: Type.Union([Type.Literal('metric'), Type.Literal('imperial')]),
  elevationUnit: Type.Union([Type.Literal('meters'), Type.Literal('feet')]),
  distanceUnit: Type.Union([Type.Literal('kilometers'), Type.Literal('miles')]),
  areaUnit: Type.Union([
    Type.Literal('hectares'),
    Type.Literal('acres'),
    Type.Literal('square_kilometers'),
    Type.Literal('square_miles'),
  ]),
  temperatureUnit: Type.Union([Type.Literal('celsius'), Type.Literal('fahrenheit')]),
  // Device-local (wifi/cellular sync behavior for THIS device) — never synced to the
  // backend; lives in the localStorage settings mirror. Optional in the synced record.
  syncPreferences: Type.Optional(SyncPreferencesSchema),
  notifications: Type.Optional(NotificationsSchema),
  privacy: Type.Optional(PrivacySchema),
  tracking: Type.Optional(TrackingSchema),
  contactSharing: Type.Optional(ContactSharingSchema),
  // Deprecated — superseded by `layers` (unified per-layer state). Kept readable
  // for one-time migration of existing accounts; no longer written.
  favoriteLayers: Type.Optional(Type.Array(Type.String())),
  panelLayers: Type.Optional(Type.Array(Type.Any())),
  /** Unified per-layer state (single source of truth): {id,enabled,visible,favorite,opacity?,options?}. */
  layers: Type.Optional(Type.Array(Type.Any())),
  /** Layer z-order overrides (drag reorder): map of layerId → order number. */
  layerOrder: Type.Optional(Type.Record(Type.String(), Type.Number())),
  /** Selected base map id. */
  baseLayer: Type.Optional(Type.String()),
})
export type UserSettings = Exact<Static<typeof UserSettingsSchema>>

// --- User ---

export const UserLocationSchema = Type.Object({
  region: Type.String({ minLength: 1, maxLength: 100 }),
  coordinates: Coordinates,
  lastUpdated: ISODate,
})

export const EmergencyContactSchema = Type.Object({
  name: Type.String({ minLength: 1, maxLength: 100 }),
  phone: Type.String({ minLength: 1, maxLength: 20 }),
  relationship: Type.String({ minLength: 1, maxLength: 50 }),
})
export type EmergencyContact = Exact<Static<typeof EmergencyContactSchema>>

export const UserSchema = Type.Object({
  id: UUID,
  email: Email,
  displayName: Type.String({ minLength: 1, maxLength: 100 }),
  givenName: Type.Optional(Type.String({ maxLength: 100 })),
  familyName: Type.Optional(Type.String({ maxLength: 100 })),
  picture: Type.Optional(Type.String({ maxLength: 2048 })),
  phone: Type.Optional(Type.String({ maxLength: 20 })),
  emergencyContact: Type.Optional(EmergencyContactSchema),
  provider: Type.Union([
    Type.Literal('google'),
    Type.Literal('apple'),
    Type.Literal('email'),
    Type.Literal('passkey'),
  ]),
  createdAt: ISODate,
  location: Type.Optional(UserLocationSchema),
  bio: Type.Optional(Type.String({ maxLength: 500 })),
  certifications: Type.Optional(Type.Array(Type.String({ minLength: 1, maxLength: 50 }))),
  settings: Type.Optional(UserSettingsSchema),
  // Sync bookkeeping: version is server-authoritative (set on pull), used only for
  // pull ordering — the user entity is last-write-wins, so it never gates a push.
  // (updatedAt is written by the storage layer and provided by the base-class type
  // constraint; not declared here to satisfy exactOptionalPropertyTypes.)
  version: Type.Optional(Type.Number()),
})
export type User = Exact<Static<typeof UserSchema>>

// --- Context-aware schemas ---

/** Fields required when creating a user */
export const UserCreateSchema = Type.Object({
  email: Email,
  displayName: Type.String({ minLength: 1, maxLength: 100 }),
  provider: Type.Union([
    Type.Literal('google'),
    Type.Literal('apple'),
    Type.Literal('email'),
    Type.Literal('passkey'),
  ]),
})

/** Partial user for updates — all fields optional */
export const UserUpdateSchema = Type.Partial(UserSchema)

/** Partial user settings for validation (any subset of fields can be provided) */
export const UserSettingsPartialSchema = Type.Partial(UserSettingsSchema)
